import React, { useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Button,
  Flex,
  Select,
  Text,
  Input,
  Checkbox,
} from '@chakra-ui/react';

const TextEditModal = ({
  isTextEditModalOpen,
  setIsTextEditModalOpen,
  currentEditingText,
  setCurrentEditingText,
  fontSize,
  setFontSize,
  textColor,
  setTextColor,
  isBold,
  setIsBold,
  isItalic,
  setIsItalic,
  backgroundColor,
  setBackgroundColor,
  borderColor,
  setBorderColor,
  dashed,
  setDashed,
  simpleColorOnly = false,
  onSave,
}) => {
  const inputRef = useRef(null);

  if (simpleColorOnly) {
    // Line / Arrow connectors have no text, font, or background — just a
    // stroke color and a solid/dashed toggle — so skip the full text-editing
    // UI entirely.
    return (
      <Modal
        isOpen={isTextEditModalOpen}
        onClose={() => setIsTextEditModalOpen(false)}
        isCentered
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Line / Arrow Color</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex alignItems="center" gap={3} mb={4}>
              <Text>Color:</Text>
              <Input
                type="color"
                value={borderColor}
                onChange={e => setBorderColor(e.target.value)}
                w="60px"
                p={0}
              />
              <Checkbox
                isChecked={dashed}
                onChange={e => setDashed(e.target.checked)}
              >
                Dashed
              </Checkbox>
            </Flex>
            <Button
              colorScheme="blue"
              onClick={() =>
                onSave({
                  text: currentEditingText,
                  fontSize,
                  textColor,
                  backgroundColor,
                  borderColor,
                  isBold,
                  isItalic,
                  dashed,
                })
              }
            >
              Save
            </Button>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isTextEditModalOpen}
      onClose={() => setIsTextEditModalOpen(false)}
      isCentered
      closeOnOverlayClick={false}
      trapFocus={false}
      blockScrollOnMount={false}
      preserveScrollBarGap={false}
      motionPreset="none"
      returnFocusOnClose={false}
      focusLock={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Text</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Input
            ref={inputRef}
            type="text"
            value={currentEditingText}
            onChange={e => setCurrentEditingText(e.target.value)}
            fontSize={`${fontSize}px`}
            fontWeight={isBold ? 'bold' : 'normal'}
            fontStyle={isItalic ? 'italic' : 'normal'}
            color={textColor}
            mb={4}
          />

          <Flex gap={4} alignItems="center" flexWrap="wrap" mb={4}>
            <Flex alignItems="center" gap={2}>
              <Text>Size:</Text>
              <Select
                value={fontSize}
                onChange={e => setFontSize(Number(e.target.value))}
                w="80px"
              >
                {[12, 14, 16, 18, 20, 24, 28, 32].map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Select>
            </Flex>

            <Flex alignItems="center" gap={2}>
              <Text>Color:</Text>
              <Input
                type="color"
                value={textColor}
                onChange={e => setTextColor(e.target.value)}
                w="50px"
                p={0}
              />
            </Flex>
            <Flex alignItems="center" gap={2}>
              <Text>Background:</Text>
              <Input
                type="color"
                value={backgroundColor}
                onChange={e => setBackgroundColor(e.target.value)}
                w="50px"
                p={0}
              />
            </Flex>
            <Flex alignItems="center" gap={2}>
              <Text>Border:</Text>
              <Input
                type="color"
                value={borderColor}
                onChange={e => setBorderColor(e.target.value)}
                w="50px"
                p={0}
              />
            </Flex>
            <Checkbox
              isChecked={isBold}
              onChange={e => setIsBold(e.target.checked)}
            >
              <strong>B</strong>
            </Checkbox>

            <Checkbox
              isChecked={isItalic}
              onChange={e => setIsItalic(e.target.checked)}
            >
              <em>I</em>
            </Checkbox>
          </Flex>

          <Button
            colorScheme="blue"
            onClick={() => {
              onSave({
                text: currentEditingText,
                fontSize,
                textColor,
                backgroundColor,
                borderColor,
                isBold,
                isItalic,
              });

              setIsTextEditModalOpen(false);
            }}
          >
            Save
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default TextEditModal;
